import { useId, useState } from 'react'
import type { FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '../hooks/useAuth'

const FIELD_CLASS =
  'mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none'

// No public sign-up: users are created by the administrator (§8.2).
export function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const destination = (location.state as { from?: string } | null)?.from ?? '/'
  const ids = { email: useId(), password: useId(), totp: useId() }

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password, totp || undefined)
      navigate(destination, { replace: true })
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'accesso non riuscito')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <form
        onSubmit={(event) => void onSubmit(event)}
        className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      >
        <h1 className="text-lg font-semibold">Movimenti RUI</h1>
        <p className="mt-1 text-sm text-slate-500">
          I movimenti del Registro Unico degli Intermediari, giorno per giorno.
        </p>

        <label className="mt-6 block text-sm font-medium text-slate-700" htmlFor={ids.email}>
          Email
        </label>
        <input
          id={ids.email}
          type="email"
          required
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className={FIELD_CLASS}
        />

        <label className="mt-4 block text-sm font-medium text-slate-700" htmlFor={ids.password}>
          Password
        </label>
        <input
          id={ids.password}
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className={FIELD_CLASS}
        />

        <label className="mt-4 block text-sm font-medium text-slate-700" htmlFor={ids.totp}>
          Codice a sei cifre <span className="font-normal text-slate-400">(se attivo)</span>
        </label>
        <input
          id={ids.totp}
          inputMode="numeric"
          pattern="\d{6}"
          autoComplete="one-time-code"
          value={totp}
          onChange={(event) => setTotp(event.target.value)}
          className={FIELD_CLASS}
        />

        {error && (
          <p role="alert" className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-6 w-full rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {submitting ? 'Accesso in corso…' : 'Entra'}
        </button>
      </form>
    </div>
  )
}
